/**
 * Process-wide event bus for MCP tool activity surfaced by the floating
 * chat (`ChatLauncher`). Pages opt in via the `useMcpActivity*` hooks in
 * `apps/console/src/hooks/useMcpActivity.ts` to react to tool calls that
 * the chat agent runs through the chat dialog (e.g. invalidate a
 * TanStack Query when an `update_blueprint` call lands).
 *
 * Design notes:
 *   - Module-singleton. No React, no provider tree — components subscribe
 *     via `useSyncExternalStore`.
 *   - One record per `toolCallId`. `publishCall` inserts a `pending`
 *     record, `publishResult` merges in `result` / `error` / `durationMs`
 *     and flips `status` to `ok` or `error`. Listeners see a stable id
 *     across both transitions, so a single "fire-once-per-call" effect
 *     can pick either the call or the result phase.
 *   - Bounded ring buffer (`MAX_EVENTS`). Oldest records drop off.
 *   - Snapshots are frozen and shared by reference until a mutation —
 *     required for `useSyncExternalStore` correctness.
 *   - Scope is intentionally chat-only for v1. Other future sources
 *     (autonomous Theopolis runs, direct MCP invocations) can publish
 *     into the same bus by populating the `source` field; consumers can
 *     filter on it.
 */

export type McpActivitySource = 'chat';

export type McpActivityStatus = 'pending' | 'ok' | 'error';

export interface IMcpActivityEvent {
  /** Stable across pending → ok/error transitions. Sourced from `toolCallId`. */
  id: string;
  /** ms since epoch at first sighting. */
  ts: number;
  source: McpActivitySource;
  /** Theopolis thread that produced this call, when known. */
  threadId: string | null;
  /**
   * Bare MCP tool name (e.g. `update_blueprint`), with any Mastra
   * server-prefix (`mcp_`) stripped. This is what predicates should
   * match on — the prefix is an implementation detail of how theopolis
   * wires its MCP client.
   */
  name: string;
  /** Original tool id as emitted by Mastra, e.g. `mcp_update_blueprint`. */
  qualifiedName: string;
  /** Args as emitted in the `tool-call` SSE frame. Unknown shape per tool. */
  args: unknown;
  status: McpActivityStatus;
  result?: unknown;
  durationMs?: number;
  error?: string;
}

/**
 * Strip Mastra's MCP server prefix (`mcp_…`) so consumers can match on the
 * bare tool name. Non-MCP tools (e.g. theopolis-local `navigate_to_page`)
 * pass through unchanged.
 */
function normalizeToolName(qualified: string): string {
  return qualified.startsWith('mcp_') ? qualified.slice('mcp_'.length) : qualified;
}

const maxEvents = 200;

let events: readonly IMcpActivityEvent[] = Object.freeze([]);
const listeners = new Set<() => void>();

function notify() {
  for (const l of listeners) {
    try {
      l();
    } catch (err) {
      // A bad listener must not poison the bus.
      console.error('mcp-activity-bus listener threw', err);
    }
  }
}

function commit(next: IMcpActivityEvent[]) {
  const trimmed = next.length > maxEvents ? next.slice(next.length - maxEvents) : next;
  // Object.freeze widens element types to `Readonly<…>` which TS sometimes
  // surfaces as optional fields downstream; cast back to the canonical
  // readonly-array type so consumers see required fields.
  events = Object.freeze(trimmed) as readonly IMcpActivityEvent[];
  notify();
}

export interface IPublishCallInput {
  toolCallId: string;
  name: string;
  args: unknown;
  threadId: string | null;
  source?: McpActivitySource;
}

export interface IPublishResultInput {
  toolCallId: string;
  result?: unknown;
  durationMs?: number;
  error?: string;
}

export const mcpActivityBus = {
  publishCall(input: IPublishCallInput): void {
    // Idempotent on toolCallId — if a call frame somehow lands twice we
    // keep the existing record (preserves `ts` and any prior result).
    if (events.some((e) => e.id === input.toolCallId)) return;
    const ev: IMcpActivityEvent = {
      id: input.toolCallId,
      ts: Date.now(),
      source: input.source ?? 'chat',
      threadId: input.threadId,
      name: normalizeToolName(input.name),
      qualifiedName: input.name,
      args: input.args,
      status: 'pending',
    };
    commit([...events, ev]);
  },

  publishResult(input: IPublishResultInput): void {
    const idx = events.findIndex((e) => e.id === input.toolCallId);
    if (idx === -1) {
      // No matching call (shouldn't normally happen — theopolis emits
      // `tool-call` before `tool-result`). Drop silently rather than
      // synthesize an orphan record with no `name` / `args`.
      return;
    }
    const prev = events[idx];
    if (!prev) return;
    const next: IMcpActivityEvent = {
      id: prev.id,
      ts: prev.ts,
      source: prev.source,
      threadId: prev.threadId,
      name: prev.name,
      qualifiedName: prev.qualifiedName,
      args: prev.args,
      status: input.error ? 'error' : 'ok',
      result: input.result,
      durationMs: input.durationMs,
      error: input.error,
    };
    const copy = events.slice();
    copy[idx] = next;
    commit(copy);
  },

  subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },

  getSnapshot(): readonly IMcpActivityEvent[] {
    return events;
  },

  /** Test-only. Drops every record and listener. */
  _reset(): void {
    events = Object.freeze([]);
    listeners.clear();
  },
};
